/**
 * Hook for Google reCAPTCHA v3
 * Usage: const { executeRecaptcha } = useRecaptcha();
 *        const token = await executeRecaptcha('login');
 * 
 * Note: If reCAPTCHA fails (domain not whitelisted, etc.), returns empty string
 * to allow form submission without blocking users.
 */

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export function useRecaptcha() {
  const executeRecaptcha = async (action) => {
    // If no site key configured, skip reCAPTCHA
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('reCAPTCHA site key not configured');
      return '';
    }

    return new Promise((resolve) => {
      // Check if grecaptcha is loaded
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        console.warn('reCAPTCHA not loaded');
        resolve('');
        return;
      }

      // Set a timeout to avoid blocking indefinitely
      const timeout = setTimeout(() => {
        console.warn('reCAPTCHA timeout');
        resolve('');
      }, 5000);

      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action })
            .then((token) => {
              clearTimeout(timeout);
              resolve(token);
            })
            .catch((error) => {
              clearTimeout(timeout);
              // Don't log error to console to avoid scary messages for users
              // The error is usually "Invalid site key" when domain is not whitelisted
              console.warn('reCAPTCHA skipped:', error.message || 'domain not whitelisted');
              resolve(''); // Don't block the form if reCAPTCHA fails
            });
        });
      } catch (error) {
        clearTimeout(timeout);
        console.warn('reCAPTCHA error:', error);
        resolve('');
      }
    });
  };

  return { executeRecaptcha };
}

export default useRecaptcha;
